/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx, keyframes } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

const GRADIENT_ANGLE_VARIABLE = '--gradient-angle';

const BORDER_ORANGE = token('color.rovo.border.saffron');
const BORDER_GREEN = token('color.rovo.border.lime');
const BORDER_BLUE = token('color.rovo.border.blue');
const BORDER_PURPLE = token('color.rovo.border.purple');

/**
 * Because the aspect ratio of the actual thinking bars is very wide,
 * the gradient will stretch out at each corner which would make the
 * rovo colours very disproportionate.
 *
 * To workaround this we use a dynamic angle amplitude to narrow the angles as we approach
 * 90 and 180 degrees, and we have a general aspect ratio of the expected design as
 * it's not worth calculating the width and height of the actual components.
 *
 * Approximating the minimum edge span as inversely proportional to the expected
 * wrapper aspect ratio.
 *
 * 360 / 48 is the approximated aspect ratio.
 */
const DEFAULT_SIDE_ANGLE_SPAN = `2 * atan(1 / (360 / 48))`;
/**
 * Squared cosine produces a symmetric 0..1 signal based on rotation angle:
 * 0 at top/bottom, 1 at left/right, which tells us how close the highlight is
 * to the side edges.
 */
const DEFAULT_SIDE_WEIGHT = `calc(cos(var(${GRADIENT_ANGLE_VARIABLE}, 39deg)) * cos(var(${GRADIENT_ANGLE_VARIABLE}, 39deg)))`;
/**
 * Interpolates the highlight span between the minimum side-edge span and a
 * wider 90deg span, with amplitude scaling and side weighting for earlier/
 * longer narrowing near the left and right edges.
 */
const DEFAULT_HIGHLIGHT_SPAN = `calc(${DEFAULT_SIDE_ANGLE_SPAN} + ((90deg - ${DEFAULT_SIDE_ANGLE_SPAN})  * ${DEFAULT_SIDE_WEIGHT}))`;

// Conic gradient that drives both the visible border and blurred glow.
const ROVO_MOSAIC_GRADIENT_BACKGROUND = `conic-gradient(
	from calc(var(${GRADIENT_ANGLE_VARIABLE}, 39deg) - (${DEFAULT_HIGHLIGHT_SPAN} / 2)) at 50% 50%,
	${BORDER_ORANGE} 0deg ${DEFAULT_HIGHLIGHT_SPAN},
	${BORDER_GREEN} ${DEFAULT_HIGHLIGHT_SPAN} 180deg,
	${BORDER_BLUE} 180deg calc(180deg + ${DEFAULT_HIGHLIGHT_SPAN}),
	${BORDER_PURPLE} calc(180deg + ${DEFAULT_HIGHLIGHT_SPAN}) 360deg
)`;

// Rotate the custom angle property so the highlight continuously sweeps around the border.
const gradientRotation = keyframes({
	'0%': { [GRADIENT_ANGLE_VARIABLE]: '0deg' },
	'100%': { [GRADIENT_ANGLE_VARIABLE]: '360deg' },
});

// @property is required so the browser knows --gradient-angle is an interpolatable
// <angle> — without it, keyframe animation of a custom property has no effect.
const gradientAngleProperty = css({
	'@property --gradient-angle': {
		syntax: '"<angle>"',
		inherits: 'false',
		initialValue: '39deg',
	},
});

const BLUR_SIZE = 32;
const BORDER_WIDTH = 1;

// Outer position context for the decorative glow and border layers.
const wrapperBaseStyle = css({
	position: 'relative',
	width: '100%',
	borderRadius: token('radius.xlarge'),
});

// Thinking shell uses pseudo-elements for the blurred glow and rotating gradient border.
const thinkingWrapperStyle = css({
	position: 'relative',
	width: '100%',
	paddingBlock: BORDER_WIDTH,
	paddingInline: BORDER_WIDTH,
	borderRadius: token('radius.xlarge'),
	isolation: 'isolate',
	'&::before': {
		content: '""',
		position: 'absolute',
		width: `calc(100% - ${BLUR_SIZE}px)`,
		height: `calc(100% - ${BLUR_SIZE}px)`,
		top: `calc(${BLUR_SIZE}px / 2)`,
		left: `calc(${BLUR_SIZE}px / 2)`,
		borderRadius: `calc(${token('radius.xlarge')} + ${BORDER_WIDTH}px)`,
		background: ROVO_MOSAIC_GRADIENT_BACKGROUND,
		animation: `${gradientRotation} 3s linear infinite`,
		filter: `blur(${BLUR_SIZE / 2}px)`,
		pointerEvents: 'none',
		zIndex: -1,
	},
	'&::after': {
		content: '""',
		position: 'absolute',
		inset: 0,
		borderRadius: `calc(${token('radius.xlarge')} + ${BORDER_WIDTH}px)`,
		background: ROVO_MOSAIC_GRADIENT_BACKGROUND,
		animation: `${gradientRotation} 3s linear infinite`,
		pointerEvents: 'none',
		zIndex: -1,
	},
	'@media (prefers-reduced-motion)': {
		'&::before': { animation: 'none' },
		'&::after': { animation: 'none' },
	},
});

const plainBorderStyle = css({
	width: '100%',
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	paddingBlock: BORDER_WIDTH,
	paddingInline: BORDER_WIDTH,
	overflow: 'hidden',
});

// Shared layout/padding base for the actual content area so children can stay mounted
// while the surrounding thinking chrome changes.
const contentBaseStyle = css({
	width: '100%',
	paddingInline: token('space.100'),
	paddingBlock: token('space.100'),
	position: 'relative',
	zIndex: 0,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface.overlay'),
	boxSizing: 'border-box',
});

const rovoSurfaceStyle = css({
	backgroundColor: token('elevation.rovo.surface.overlay'),
});

export type RovoThinkingBarWrapperProps = {
	children: React.ReactNode;
	/**
	 * When `false`, the animated mosaic gradient border and glow effect are
	 * omitted and only the plain content area is rendered.
	 */
	isThinking?: boolean;
};

/**
 * Container wrapper for RovoThinkingBar that provides the animated mosaic
 * gradient border and subtle glow effect.
 *
 * Set `isThinking={false}` to render only the plain content area
 * without the animated border or glow.
 */
export const RovoThinkingBarWrapper = ({
	children,
	isThinking,
}: RovoThinkingBarWrapperProps): JSX.Element => {
	return (
		<div
			css={[
				wrapperBaseStyle,
				isThinking && gradientAngleProperty,
				isThinking && thinkingWrapperStyle,
				!isThinking && plainBorderStyle,
				!isThinking && fg('platform_editor_ai_rovo_bar_ui_update') && rovoSurfaceStyle,
			]}
			data-testid={
				isThinking ? 'rovo-thinking-bar-wrapper__thinking' : 'rovo-thinking-bar-wrapper__idle'
			}
		>
			<div
				css={[contentBaseStyle, fg('platform_editor_ai_rovo_bar_ui_update') && rovoSurfaceStyle]}
			>
				{children}
			</div>
		</div>
	);
};
